export type BreadcrumbItem = { label: string; to?: string }

const NATIONAL_BREADCRUMB_ITEM: BreadcrumbItem = { label: 'Vue nationale', to: '/' }

export type BreadcrumbRouteKeys = {
  dir: string
  dom: string
  age: string
  equ: string
}

export type BreadcrumbTrailLabels = {
  dirLabel: string
  domLabel: string
  ageLabel: string
  equLabel?: string
}

/** Libellé métier : court trimé, sinon long trimé, sinon valeur brute, sinon repli. */
export function resolveEntityLabel(
  primary: string | null | undefined,
  longLabel: string | null | undefined,
  fallback: string
): string {
  return primary?.trim() || longLabel?.trim() || primary || fallback
}

export function buildOrganizationBasePath(
  dir: string,
  dom?: string,
  age?: string,
  equ?: string
): string {
  const parts = [dir]
  if (dom) parts.push(dom)
  if (age) parts.push(age)
  if (equ) parts.push(equ)
  return '/' + parts.map(encodeURIComponent).join('/')
}

export function isEquipeDeDirection(keys: BreadcrumbRouteKeys): boolean {
  return keys.dir === keys.dom && keys.dom === keys.age && keys.age === keys.equ
}

export function isEquipeDeDomaine(keys: BreadcrumbRouteKeys): boolean {
  return keys.dom === keys.age && keys.age === keys.equ
}

export function buildDirectionTrail(dirLabel: string): BreadcrumbItem[] {
  return [NATIONAL_BREADCRUMB_ITEM, { label: dirLabel }]
}

/**
 * Fil d'Ariane d'une URL équipe (direction / domaine / agence / équipe).
 * Le segment agence pointe toujours vers la vue « niveau agence » (…/age/age).
 */
export function buildEquipeTrail(
  keys: BreadcrumbRouteKeys,
  labels: BreadcrumbTrailLabels
): BreadcrumbItem[] {
  if (isEquipeDeDirection(keys)) {
    return buildDirectionTrail(labels.dirLabel)
  }
  const directionLink = { label: labels.dirLabel, to: `/${encodeURIComponent(keys.dir)}` }
  if (isEquipeDeDomaine(keys)) {
    return [NATIONAL_BREADCRUMB_ITEM, directionLink, { label: labels.domLabel }]
  }
  const isOnEquipeLeader = keys.equ === keys.age
  const agenceSegment: BreadcrumbItem = isOnEquipeLeader
    ? { label: labels.ageLabel }
    : {
        label: labels.ageLabel,
        to: buildOrganizationBasePath(keys.dir, keys.dom, keys.age, keys.age),
      }
  const trail: BreadcrumbItem[] = [
    NATIONAL_BREADCRUMB_ITEM,
    directionLink,
    {
      label: labels.domLabel,
      to: `/${encodeURIComponent(keys.dir)}?highlightDomaine=${encodeURIComponent(keys.dom)}`,
    },
    agenceSegment,
  ]
  if (isOnEquipeLeader) {
    return trail
  }
  return [...trail, { label: labels.equLabel ?? keys.equ }]
}

/** Nombre de segments du squelette (4 ou 5 selon la vue agence / équipe). */
export function expectedSkeletonSegmentCount(keys: BreadcrumbRouteKeys): number {
  if (!keys.dir || !keys.dom || !keys.age || !keys.equ) return 4
  if (isEquipeDeDirection(keys) || isEquipeDeDomaine(keys)) return 4
  return keys.equ === keys.age ? 4 : 5
}
